import { strings } from "../languages/localizationStrings";

/**
 * IRIS Personality Engine
 *
 * Returns contextual phrases for IRIS the owl mascot based on
 * user stats, time of day, and current screen context.
 */

function getTimeOfDay() {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return 'morning';
    if (hour >= 12 && hour < 17) return 'afternoon';
    if (hour >= 17 && hour < 21) return 'evening';
    return 'night';
}

/**
 * Get IRIS greeting for the landing page.
 * Picks context-aware phrase based on user progress.
 */
export function getIrisGreeting(stats) {
    if (!stats || stats.totalSessions === 0) {
        return strings.irisGreetingFirst;
    }

    const todayStr = new Date().toISOString().split('T')[0];
    const completedToday = stats.lastCompletedDate === todayStr;

    if (completedToday) {
        return strings.irisGreetingDone;
    }

    if (stats.currentStreak > 0) {
        return strings.formatString(strings.irisGreetingStreak, stats.currentStreak);
    }

    return strings.irisGreetingReturn;
}

/**
 * Get IRIS message for the dashboard.
 * Reacts to streak length and total sessions.
 */
export function getIrisDashboardMessage(stats) {
    if (!stats || stats.totalSessions === 0) {
        return strings.irisDashboardEmpty;
    }

    if (stats.totalSessions >= 200) {
        return strings.irisDashboardLegend || strings.irisDashboardChampion;
    }

    if (stats.totalSessions >= 50) {
        return strings.irisDashboardChampion;
    }

    if (stats.currentStreak >= 8) {
        return strings.formatString(strings.irisDashboardHigh, stats.currentStreak);
    }

    if (stats.currentStreak >= 4) {
        return strings.irisDashboardMedium;
    }

    return strings.irisDashboardLow;
}

/**
 * Get IRIS celebration message after completing an exercise.
 */
export function getIrisCompletionMessage(progress) {
    if (!progress) {
        return strings.irisCompletionNormal;
    }

    if (progress.totalSessions === 1) {
        return strings.irisCompletionFirst;
    }

    // Milestones
    const milestones = [365, 200, 100, 50, 25, 10];
    for (const m of milestones) {
        if (progress.totalSessions === m) {
            return strings.formatString(strings.irisCompletionMilestone, m);
        }
    }

    if (progress.currentStreak >= 3) {
        return strings.formatString(strings.irisCompletionStreak, progress.currentStreak);
    }

    return strings.irisCompletionNormal;
}

/**
 * Get IRIS mood based on context.
 * Returns: 'happy' | 'proud' | 'excited' | 'sleepy' | 'neutral'
 */
export function getIrisMood(stats) {
    if (!stats || stats.totalSessions === 0) return 'neutral';

    const todayStr = new Date().toISOString().split('T')[0];
    const completedToday = stats.lastCompletedDate === todayStr;
    const timeOfDay = getTimeOfDay();

    if (completedToday) return 'proud';
    if (stats.currentStreak >= 7) return 'excited';
    if (timeOfDay === 'night') return 'sleepy';
    return 'happy';
}
